import { useLocation, Link } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import { getPageContext } from '@/shared/utils/chatWidgetUtils';
import { WIDGET_TEXTAREA } from '@/shared/utils/chatHelpers';
import babyBili from '@/assets/img/icons/svg/babyBili.svg';

import useWidgetChat from '../hooks/useWidgetChat';

import ChatInput from './ChatInput';
import ChatMessages from './ChatMessages';
import '../ChatPill.css';
import './ChatWidget.css';

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [input, setInput] = useState('');
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const location = useLocation();
    const { user } = useAuth();
    const { chatRequest, clearChatRequest } = useChatPageState();

    // Keep the widget slightly above the footer when it scrolls into view
    const updateFooterOffset = useCallback(() => {
        const footer = document.getElementById('App-footer');
        if (!footer || !containerRef.current) return;
        const footerRect = footer.getBoundingClientRect();
        const overlap = window.innerHeight - footerRect.top;
        const offset = Math.max(0, overlap - 3);
        containerRef.current.style.setProperty(
            '--footer-offset',
            `${offset}px`
        );
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', updateFooterOffset, {
            passive: true,
        });
        window.addEventListener('resize', updateFooterOffset, {
            passive: true,
        });
        updateFooterOffset();
        return () => {
            window.removeEventListener('scroll', updateFooterOffset);
            window.removeEventListener('resize', updateFooterOffset);
        };
    }, [updateFooterOffset]);

    const {
        messages,
        loading,
        messagesEndRef,
        threadId,
        connect,
        disconnect,
        send,
        promote,
    } = useWidgetChat();

    const pageContext = getPageContext(location.pathname);

    // Respond to external openChat(message) requests (e.g., from tile buttons).
    // chatRequest.id changes on each new request; the null-clear triggers a
    // second run that exits early via the guard.
    useEffect(() => {
        if (!chatRequest) return;
        setOpen(true);
        setInput(chatRequest.message);
        clearChatRequest();
        if (user) connect();
        // Focus the input after a short delay to let the panel animate in
        const focusTimer = setTimeout(() => inputRef.current?.focus(), 150);
        return () => clearTimeout(focusTimer);
    }, [chatRequest, clearChatRequest, connect, user]);

    // Don't render widget on hidden pages
    if (!pageContext.visible) return null;

    const handleToggle = () => {
        if (!open) {
            setOpen(true);
            if (user) {
                connect();
            }
        } else {
            setOpen(false);
            setExpanded(false);
            disconnect();
        }
    };

    const handleSend = () => {
        if (!input.trim() || loading) return;
        send(input);
        setInput(''); // ChatInput resets height via useEffect
    };

    // Collapsed state - show floating button with prompt bubble
    if (!open) {
        return (
            <div className="chat-widget-container" ref={containerRef}>
                {pageContext.promptText && (
                    <div className="chat-widget-prompt bg-white text-muted">
                        {pageContext.promptText}
                    </div>
                )}
                <Button
                    className="chat-widget-button btn-primary"
                    onClick={handleToggle}
                    aria-label="Open chat assistant"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                    >
                        <path
                            d={
                                'M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' +
                                'm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' +
                                'm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z'
                            }
                        />
                        <path
                            d={
                                'm2.165 15.803.02-.004c1.83-.363 ' +
                                '2.948-.842 3.468-1.105A9.06 9.06 ' +
                                '0 0 0 8 15c4.418 0 8-3.134 8-7s' +
                                '-3.582-7-8-7-8 3.134-8 7c0 1.76' +
                                '.743 3.37 1.97 4.6a10.437 10.437 ' +
                                '0 0 1-.524 2.318l-.003.011a10.722 ' +
                                '10.722 0 0 1-.244.637c-.079.186' +
                                '.074.394.272.362a21.673 21.673 ' +
                                '0 0 0 .693-.125zm.8-3.108a1 1 0 ' +
                                '0 0-.287-.801C1.618 10.83 1 9.468 ' +
                                '1 8c0-3.192 3.004-6 7-6s7 2.808 ' +
                                '7 6c0 3.193-3.004 6-7 6a8.06 8.06 ' +
                                '0 0 1-2.088-.272 1 1 0 0 0-.711' +
                                '.074c-.387.196-1.24.57-2.634.893' +
                                'a10.97 10.97 0 0 0 .398-2z'
                            }
                        />
                    </svg>
                </Button>
            </div>
        );
    }

    // Expanded state - show chat panel
    const panelClass = `chat-widget-panel border-0${expanded ? ' chat-widget-expanded' : ''}`;

    return (
        <div className="chat-widget-container" ref={containerRef}>
            <Card className={panelClass}>
                {/* Header */}
                <div
                    className={
                        'chat-widget-header flex-shrink-0 bg-primary text-white ' +
                        'px-3 pb-3 pt-4 d-flex justify-content-between ' +
                        'align-items-center'
                    }
                >
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-warning border border-primary rounded-3 d-flex align-items-center justify-content-center widget-bili-avatar">
                            <img
                                src={babyBili}
                                alt="Bili"
                                className="widget-bili-icon"
                            />
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold text-white">Bili</h6>
                            <small className="text-white opacity-75">
                                Sustainability Assistant
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="link"
                            className="text-white p-0"
                            onClick={() => setExpanded(!expanded)}
                            aria-label={
                                expanded
                                    ? 'Collapse chat panel'
                                    : 'Expand chat panel'
                            }
                        >
                            {expanded ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d={
                                            'M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 ' +
                                            '1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 ' +
                                            '15.121a.5.5 0 0 0 0 .707zM15.828.172a.5.5 0 0 0-.707 0L11.025 ' +
                                            '4.268V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 ' +
                                            '0 0-1h-2.768L15.828.879a.5.5 0 0 0 0-.707z'
                                        }
                                    />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d={
                                            'M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 ' +
                                            '0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096' +
                                            '-4.096a.5.5 0 0 0 0-.707zm4.344-4.344a.5.5 0 0 0 .707 ' +
                                            '0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5' +
                                            'a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707z'
                                        }
                                    />
                                </svg>
                            )}
                        </Button>
                        <Button
                            variant="link"
                            className="text-white p-0"
                            onClick={handleToggle}
                            aria-label="Close chat assistant"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                            >
                                <path
                                    d={
                                        'M4.646 4.646a.5.5 0 0 1 .708 ' +
                                        '0L8 7.293l2.646-2.647a.5.5 0 ' +
                                        '0 1 .708.708L8.707 8l2.647 ' +
                                        '2.646a.5.5 0 0 1-.708.708L8 ' +
                                        '8.707l-2.646 2.647a.5.5 0 0 ' +
                                        '1-.708-.708L7.293 8 4.646 ' +
                                        '5.354a.5.5 0 0 1 0-.708z'
                                    }
                                />
                            </svg>
                        </Button>
                    </div>
                </div>

                {/* Body - either sign-in prompt or chat */}
                {!user ? (
                    <div className="chat-widget-signin flex-grow-1 d-flex align-items-center justify-content-center p-4">
                        <div className="text-center">
                            <p className="text-muted mb-3">
                                Sign in to chat with Bili, your sustainability
                                assistant.
                            </p>
                            <Button
                                as={Link}
                                to="/login"
                                variant="primary"
                                className="rounded-pill"
                                onClick={() => setOpen(false)}
                            >
                                Sign In to Chat
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="chat-widget-messages flex-grow-1 overflow-auto bg-light p-3">
                            <ChatMessages
                                messages={messages}
                                loading={loading}
                                loadingLabel="Thinking..."
                                messagesEndRef={messagesEndRef}
                            />
                        </div>
                        <div className="chat-widget-input flex-shrink-0 bg-primary p-2">
                            <ChatInput
                                ref={inputRef}
                                value={input}
                                onValueChange={setInput}
                                onSend={handleSend}
                                disabled={loading}
                                placeholder={
                                    pageContext.promptText ||
                                    'Ask a question...'
                                }
                                minHeight={WIDGET_TEXTAREA.minPx}
                                maxHeight={WIDGET_TEXTAREA.maxPx}
                            />
                        </div>
                        {messages.some((m) => m.sender === 'user') &&
                            threadId && (
                                <div className="chat-widget-continue flex-shrink-0 bg-primary text-center pt-1 pb-2">
                                    <Link
                                        to={`/bili-chat?thread=${encodeURIComponent(threadId)}`}
                                        state={{ promotedMessages: messages }}
                                        className="small text-white text-decoration-none"
                                        onClick={() => {
                                            promote();
                                            setOpen(false);
                                            disconnect();
                                        }}
                                    >
                                        Continue in Bili Chat &rarr;
                                    </Link>
                                </div>
                            )}
                    </>
                )}
            </Card>
        </div>
    );
}
